import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Send, FileText } from 'lucide-react'
import toast from 'react-hot-toast'
import cd2bLevantamientoService from '@/services/cd2bLevantamientoService'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import PageContainer from '@/components/common/PageContainer'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select'
import { leerParaEnviar } from '@/lib/archivos'

/** ¿Se cumple la condición de una pregunta/sección con las respuestas actuales? */
function cumple(cond, r) {
  if (!cond) return true
  const v = r[cond.clave]
  if (cond.respondida) return v !== undefined && v !== ''
  if (cond.igual !== undefined) return v === cond.igual || String(v) === String(cond.igual)
  if (cond.mayorQue !== undefined) return Number(v) > Number(cond.mayorQue)
  return true
}

const NIVEL_VARIANT = { bloqueo: 'danger', aviso: 'warning', info: 'default' }

/** Cuántas actividades se listan al buscar. Son 774: pintarlas todas no ayuda a nadie. */
const MAX_RESULTADOS_ACTIVIDAD = 40

/**
 * Buscador de actividad económica (catálogo CAT-019 del Ministerio de Hacienda). Mismo patrón
 * que `IdentidadFiscal.jsx`: no se deja escribir texto libre — un código que no existe en el
 * catálogo pasa cualquier validación de formulario y lo rechaza Hacienda al recibir el DTE.
 */
function CampoActividad({ value, onChange, actividades }) {
  const [busqueda, setBusqueda] = useState('')
  const seleccionada = useMemo(
    () => actividades.find((a) => a.codigo === value) || null,
    [actividades, value],
  )
  const resultados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    if (!q) return []
    return actividades
      .filter((a) => a.codigo.includes(q) || a.valor.toLowerCase().includes(q))
      .slice(0, MAX_RESULTADOS_ACTIVIDAD)
  }, [actividades, busqueda])

  if (seleccionada) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <code className="rounded bg-surface-muted px-2 py-1 font-mono text-caption text-foreground">{seleccionada.codigo}</code>
        <span className="min-w-0 flex-1 text-body-sm text-foreground">{seleccionada.valor}</span>
        <button type="button" className="inline-flex min-h-10 items-center px-1 text-caption text-primary underline"
          onClick={() => { onChange(''); setBusqueda('') }}>
          Cambiar
        </button>
      </div>
    )
  }
  return (
    <>
      <Input value={busqueda} placeholder="Busca por nombre o código: «restaurante», «56101»…"
        onChange={(ev) => setBusqueda(ev.target.value)} />
      {resultados.length > 0 && (
        <ul className="mt-1 max-h-56 divide-y divide-line overflow-y-auto rounded-md border border-line bg-card">
          {resultados.map((a) => (
            <li key={a.codigo}>
              <button type="button"
                className="min-h-10 w-full px-3 py-2 text-left text-body-sm text-foreground hover:bg-surface-muted"
                onClick={() => { onChange(a.codigo); setBusqueda('') }}>
                <code className="mr-2 font-mono text-caption text-subtle">{a.codigo}</code>
                {a.valor}
              </button>
            </li>
          ))}
        </ul>
      )}
      {busqueda.trim() && resultados.length === 0 && (
        <p className="mt-1 text-caption text-subtle">
          Ninguna actividad del catálogo coincide. No se puede inventar un código: uno que no
          existe pasa toda validación y lo rechaza Hacienda al recibir el documento.
        </p>
      )}
    </>
  )
}

/**
 * Cuestionario de levantamiento visto por el ASESOR: lo llena durante la entrevista, ve en vivo
 * qué paquete deducen las respuestas y, cuando le cuadra, arma la cotización.
 *
 * Las preguntas vienen del servidor (`cd2b-levantamiento.json`), así que añadir una no obliga a
 * tocar esta pantalla.
 */
export default function LevantamientoEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [cat, setCat] = useState(null)
  const [lev, setLev] = useState(null)
  const [giros, setGiros] = useState([])
  // Nombres que el asesor ve en la cotización, por código interno (edición o módulo).
  const [nombres, setNombres] = useState({})
  const [r, setR] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [actividades, setActividades] = useState([])

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const [c, l, cot] = await Promise.all([
        cd2bLevantamientoService.getCatalogo(),
        cd2bLevantamientoService.getById(id),
        cd2bPropuestaService.getCatalogo().catch(() => null),
      ])
      setCat(c?.data || null)
      setLev(l?.data || null)
      setR(l?.data?.respuestas || {})
      setGiros((cot?.data?.giros || []).map((g) => ({ valor: g.id, label: g.label })))
      setNombres({
        ...Object.fromEntries((cot?.data?.ediciones || []).map((e) => [e.edicion, e.label])),
        ...Object.fromEntries((cot?.data?.modulos || []).map((m) => [m.code, m.label])),
      })
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo cargar el cuestionario')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { cargar() }, [cargar])

  // Catálogo fiscal (actividad económica CAT-019): petición propia, best-effort — si falla, el
  // campo sale sin resultados y lo dice, no se inventa un catálogo local de respaldo.
  useEffect(() => {
    let vivo = true
    cd2bPropuestaService.getCatalogosFiscales()
      .then((res) => { if (vivo) setActividades(res?.data?.actividadEconomica || []) })
      .catch(() => { if (vivo) setActividades([]) })
    return () => { vivo = false }
  }, [])

  const set = (clave, valor) => setR((prev) => ({ ...prev, [clave]: valor }))

  const guardar = async () => {
    setSaving(true)
    try {
      const res = await cd2bLevantamientoService.update(id, { respuestas: r })
      setLev(res?.data || lev)
      toast.success('Respuestas guardadas')
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo guardar')
    } finally {
      setSaving(false)
    }
  }

  const enviar = async () => {
    try {
      const res = await cd2bLevantamientoService.enviar(id)
      try { await navigator.clipboard.writeText(res.data.url) } catch { /* noop */ }
      toast.success('Cuestionario enviado al cliente (enlace copiado)')
      cargar()
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo enviar')
    }
  }

  const armar = async () => {
    try {
      await guardar()
      const res = await cd2bLevantamientoService.armarCotizacion(id)
      toast.success(res.data.ya ? 'Esta cotización ya existía' : 'Cotización armada')
      navigate(`/consultor/cotizaciones/${res.data.propuestaId}`)
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo armar la cotización')
    }
  }

  if (loading) {
    return <PageContainer><p className="py-8 text-center text-body-sm text-subtle">Cargando…</p></PageContainer>
  }
  if (!cat || !lev) {
    return <PageContainer><p className="py-8 text-center text-body-sm text-subtle">No se encontró el cuestionario.</p></PageContainer>
  }

  const campo = (p) => {
    const v = r[p.clave]
    if (p.tipo === 'si_no') {
      return (
        <div className="flex gap-2">
          <Button type="button" size="sm" className="min-w-16" aria-pressed={v === true} variant={v === true ? 'default' : 'outline'} onClick={() => set(p.clave, true)}>Sí</Button>
          <Button type="button" size="sm" className="min-w-16" aria-pressed={v === false} variant={v === false ? 'default' : 'outline'} onClick={() => set(p.clave, false)}>No</Button>
        </div>
      )
    }
    if (p.tipo === 'actividad') {
      return <CampoActividad value={v || ''} onChange={(codigo) => set(p.clave, codigo)} actividades={actividades} />
    }
    if (p.tipo === 'opcion' || p.tipo === 'rango' || p.tipo === 'giro') {
      const ops = p.tipo === 'giro' ? giros : (p.opciones || [])
      return (
        <Select value={v != null ? String(v) : ''} onValueChange={(x) => set(p.clave, x)}>
          <SelectTrigger><SelectValue placeholder="Elige una opción…" /></SelectTrigger>
          <SelectContent>
            {ops.map((o) => <SelectItem key={o.valor} value={String(o.valor)}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )
    }
    if (p.tipo === 'multi') {
      const marcados = Array.isArray(v) ? v : []
      return (
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          {(p.opciones || []).map((o) => (
            <label key={o.valor} className="flex min-h-10 cursor-pointer items-center gap-2.5 text-body-sm text-foreground">
              <Checkbox checked={marcados.includes(o.valor)}
                onCheckedChange={(marcar) => set(p.clave, marcar === true
                  ? [...marcados, o.valor]
                  : marcados.filter((x) => x !== o.valor))} />
              {o.label}
            </label>
          ))}
        </div>
      )
    }
    if (p.tipo === 'textarea') {
      return (
        <Textarea rows={3} value={v || ''} onChange={(e) => set(p.clave, e.target.value)} />
      )
    }
    // `archivo` — el cuestionario podía preguntar cualquier cosa menos pedir un archivo, así
    // que la respuesta a «¿tienes tu catálogo de cuentas?» se quedaba en un sí sin adjunto.
    // Se guarda el CONTENIDO en la respuesta (los archivos del levantamiento son CSV de unos
    // pocos KB) para que viaje con ella y el aprovisionamiento pueda cargarlo sin un segundo
    // viaje ni un almacén temporal que caduque.
    //
    // El contenido va con su `codificacion`: un `.xlsx` es un ZIP, y leerlo como texto lo
    // destruye (ver `@/lib/archivos`). Antes se leía todo con `f.text()` y el catálogo del
    // contador en Excel —el formato más probable— llegaba corrupto y se perdía en silencio.
    if (p.tipo === 'archivo') {
      const guardado = v && typeof v === 'object' ? v : null
      return (
        <div className="flex flex-col gap-1.5">
          <input
            type="file"
            accept=".csv,.tsv,.txt,.xlsx,.xlsm"
            className="w-full text-body-sm text-foreground file:mr-3 file:min-h-10 file:rounded-md file:border file:border-solid file:border-line file:bg-card file:px-3 file:text-body-sm file:font-medium file:text-foreground"
            onChange={async (ev) => {
              const f = ev.target.files?.[0]
              if (!f) return set(p.clave, null)
              try {
                set(p.clave, await leerParaEnviar(f))
              } catch {
                toast.error('No se pudo leer el archivo. Intenta guardarlo como CSV.')
                set(p.clave, null)
              }
            }}
          />
          {guardado && (
            <span className="break-all text-body-sm text-subtle">
              {guardado.nombre} · {Math.max(1, Math.round((guardado.tamano || 0) / 1024))} KB
            </span>
          )}
        </div>
      )
    }
    const tipo = p.tipo === 'numero' ? 'number' : p.tipo === 'fecha' ? 'date' : 'text'
    return (
      <Input type={tipo} value={v ?? ''}
        onChange={(e) => set(p.clave, tipo === 'number' ? Number(e.target.value) : e.target.value)} />
    )
  }

  const sug = lev.sugerencia

  return (
    <PageContainer>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/consultor/levantamientos')} aria-label="Volver">
          <ArrowLeft size={18} />
        </Button>
        <div className="min-w-0">
          <h2 className="truncate font-display text-h2 font-semibold text-foreground">{lev.empresaNombre}</h2>
          <p className="truncate text-caption text-subtle">
            {lev.clienteEmail || 'Sin correo de contacto'}
            {lev.respondidoPor === 'cliente' && ' · respondido por el cliente'}
          </p>
        </div>
      </div>

      {/* Lo que el motor deduce de las respuestas. Es una propuesta: se ajusta en la cotización. */}
      {sug?.edicion && (
        <Card>
          <CardHeader><CardTitle className="text-h3">Paquete sugerido</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="accent">{nombres[sug.edicion] || sug.edicion}</Badge>
              {(sug.modulos || []).map((m) => <Badge key={m} variant="secondary">{nombres[m] || nombres[m.replace('cd2b-mod-', '')] || m.replace('cd2b-mod-', '')}</Badge>)}
            </div>
            {(sug.seats?.completo > 0 || sug.seats?.operativo > 0) && (
              <p className="text-body-sm text-subtle">
                Asientos por encima del plan: {sug.seats.completo} completo(s), {sug.seats.operativo} operativo(s).
              </p>
            )}
            {(sug.capacidad || []).length > 0 && (
              <p className="text-body-sm text-subtle">
                Cupo extra: {sug.capacidad.map((c) => `${c.cantidad}× ${c.code.replace('cd2b-', '')}`).join(', ')}.
              </p>
            )}
            {(sug.avisos || []).map((a) => (
              <Alert key={a.code} variant={NIVEL_VARIANT[a.nivel] || 'default'}>
                <AlertDescription>{a.texto}</AlertDescription>
              </Alert>
            ))}
            <p className="text-caption text-subtle">
              Es una sugerencia: al armar la cotización puedes ajustar todo y ver el precio en vivo.
            </p>
          </CardContent>
        </Card>
      )}

      {(cat.secciones || []).filter((s) => cumple(s.condicion, r)).map((s) => (
        <Card key={s.id}>
          <CardHeader><CardTitle className="text-h3">{s.titulo}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {s.ayuda && <p className="text-body-sm text-subtle">{s.ayuda}</p>}
            <div className="grid gap-4 sm:grid-cols-2">
              {(s.preguntas || []).filter((p) => cumple(p.condicion, r)).map((p) => (
                <div key={p.clave} className={p.tipo === 'multi' || p.tipo === 'textarea' || p.tipo === 'actividad' ? 'sm:col-span-2' : ''}>
                  <Label>{p.label}{p.requerida && <span className="text-danger-700 dark:text-danger-300"> *</span>}</Label>
                  {p.ayuda && <p className="mb-1 text-caption text-subtle">{p.ayuda}</p>}
                  {campo(p)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* En el teléfono la barra se queda JUSTO encima del menú de abajo (56 px + zona segura); en
          escritorio, pegada al borde. */}
      <div className="sticky bottom-[calc(56px+env(safe-area-inset-bottom))] z-30 flex flex-wrap justify-end gap-2 border-t border-line bg-card/95 py-3 backdrop-blur md:bottom-0">
        {lev.clienteEmail && lev.estado !== 'aplicado' && (
          <Button variant="outline" className="flex-1 sm:flex-none" onClick={enviar}>
            <Send size={16} /> <span className="sm:hidden">Enviar</span><span className="hidden sm:inline">Enviárselo al cliente</span>
          </Button>
        )}
        <Button variant="outline" className="flex-1 sm:flex-none" onClick={guardar} disabled={saving}>
          <Save size={16} /> {saving ? 'Guardando…' : 'Guardar'}
        </Button>
        <Button className="w-full sm:w-auto" onClick={armar}>
          <FileText size={16} /> {lev.propuestaId ? 'Ver cotización' : 'Armar cotización'}
        </Button>
      </div>
    </PageContainer>
  )
}
